import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import HTML from 'html-parse-stringify';
import { cloneElement, createElement, isValidElement } from 'react';
var keepArray = [
    'br',
    'strong',
    'i',
    'p'
];
/**
 * The content of that file is based on https://github.com/i18next/react-i18next/blob/master/src/TransWithoutContext.js
 * It was adapted to remove options and make it compatible with typescript/eslint and our Locales.
 */ /**
 * Escape literal `<` characters that are not part of valid tags recognized by the Translation system.
 * Valid tags are: numbered tags like `<0>`, `</0>`, or named tags from `keepArray` (e.g., `<strong>`, `<br/>`).
 * Any other `<` is replaced with `&lt;` so that `html-parse-stringify` does not interpret user data
 * (e.g., a source name containing `<h1>`) as real HTML tags.
 *
 * Ported from react-i18next's TransWithoutContext.js `escapeLiteralLessThan` function.
 * @see https://github.com/i18next/react-i18next/blob/master/src/TransWithoutContext.js
 */ export var escapeLiteralLessThan = function escapeLiteralLessThan(str) {
    if (!str) {
        return str;
    }
    var result = '';
    var i = 0;
    while(i < str.length){
        if (str[i] === '<') {
            var isValidTag = false;
            // Check for closing tag: </number> or </keepArrayName>
            var closingMatch = str.slice(i).match(/^<\/(\d+|[a-zA-Z][a-zA-Z0-9-]*)>/);
            if (closingMatch) {
                var tagName = closingMatch[1];
                if (/^\d+$/.test(tagName) || keepArray.includes(tagName)) {
                    isValidTag = true;
                    result += closingMatch[0];
                    i += closingMatch[0].length;
                }
            }
            if (!isValidTag) {
                // Check for opening/self-closing tag: <number>, <keepArrayName>, <number />, <keepArrayName/>
                var openingMatch = str.slice(i).match(/^<(\d+|[a-zA-Z][a-zA-Z0-9-]*)(?:\s[^>]*)?\s*(\/)?>/);
                if (openingMatch) {
                    var tagName1 = openingMatch[1];
                    if (/^\d+$/.test(tagName1) || keepArray.includes(tagName1)) {
                        isValidTag = true;
                        result += openingMatch[0];
                        i += openingMatch[0].length;
                    }
                }
            }
            if (!isValidTag) {
                result += '&lt;';
                i += 1;
            }
        } else {
            result += str[i];
            i += 1;
        }
    }
    return result;
};
var hasChildren = function hasChildren(node) {
    var checkLength = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    if (!node) {
        return false;
    }
    var base = node.props ? node.props.children : node.children;
    if (checkLength) {
        return base.length > 0;
    }
    return !!base;
};
var getChildren = function getChildren(node) {
    if (!node) {
        return [];
    }
    return node.props ? node.props.children : node.children;
};
var hasValidReactChildren = function hasValidReactChildren(children) {
    if (Object.prototype.toString.call(children) !== '[object Array]') {
        return false;
    }
    return children.every(function(child) {
        return isValidElement(child);
    });
};
var getAsArray = function getAsArray(data) {
    return Array.isArray(data) ? data : [
        data
    ];
};
var mergeProps = function mergeProps(source, target) {
    var newTarget = _object_spread({}, target);
    // overwrite source.props when target.props already set
    newTarget.props = Object.assign(source.props, target.props);
    return newTarget;
};
export var nodesToString = function nodesToString1(children) {
    if (!children) {
        return '';
    }
    var stringNode = '';
    // do not use `React.Children.toArray`, will fail at object children
    var childrenArray = getAsArray(children);
    // e.g. lorem <br/> ipsum dolor <strong>bold</strong> amet
    childrenArray.forEach(function(child, childIndex) {
        if (typeof child === 'string') {
            // actual e.g. lorem
            // expected e.g. lorem
            stringNode += "".concat(child);
        } else if (isValidElement(child)) {
            var childPropsCount = Object.keys(child.props).length;
            var shouldKeepChild = keepArray.indexOf(child.type) > -1;
            var childChildren = child.props.children;
            if (!childChildren && shouldKeepChild && childPropsCount === 0) {
                // actual e.g. lorem <br/> ipsum
                // expected e.g. lorem <br/> ipsum
                stringNode += "<".concat(child.type, "/>");
            } else if (!childChildren && (!shouldKeepChild || childPropsCount !== 0)) {
                // actual e.g. lorem <hr className="test" /> ipsum
                // expected e.g. lorem <0></0> ipsum
                stringNode += "<".concat(childIndex, "></").concat(childIndex, ">");
            } else if (child.props.i18nIsDynamicList) {
                // we got a dynamic list like
                // e.g. <ul i18nIsDynamicList>{['a', 'b'].map(item => ( <li key={item}>{item}</li> ))}</ul>
                // expected e.g. "<0></0>", not e.g. "<0><0>a</0><1>b</1></0>"
                stringNode += "<".concat(childIndex, "></").concat(childIndex, ">");
            } else if (shouldKeepChild && childPropsCount === 1 && typeof childChildren === 'string') {
                // actual e.g. dolor <strong>bold</strong> amet
                // expected e.g. dolor <strong>bold</strong> amet
                stringNode += "<".concat(child.type, ">").concat(childChildren, "</").concat(child.type, ">");
            } else {
                // regular case mapping the inner children
                var content = nodesToString(childChildren);
                stringNode += "<".concat(childIndex, ">").concat(content, "</").concat(childIndex, ">");
            }
        } else if (child === null) {
            console.warn("Trans: the passed in value is invalid - seems you passed in a null child.");
        } else {
            console.warn("Translation: the passed in value is invalid.", child);
        }
    });
    return stringNode;
};
export var renderNodes = function renderNodes(children, targetString) {
    if (targetString === '') {
        return [];
    }
    var data = {};
    var getData = function getData1(childs) {
        var childrenArray = getAsArray(childs);
        childrenArray.forEach(function(child) {
            if (typeof child === 'string') {
                return;
            }
            if (hasChildren(child)) {
                getData(getChildren(child));
            } else if ((typeof child === "undefined" ? "undefined" : _type_of(child)) === 'object' && !isValidElement(child)) {
                Object.assign(data, child);
            }
        });
    };
    getData(children);
    var escapedString = escapeLiteralLessThan(targetString);
    // parse ast from string with additional wrapper tag
    // -> avoids issues in parser removing prepending text nodes
    var ast = HTML.parse("<0>".concat(escapedString, "</0>"));
    var renderInner = function renderInner(child, node, rootReactNode) {
        var childs = getChildren(child);
        var mappedChildren = mapAST(childs, node.children, rootReactNode);
        return hasValidReactChildren(childs) && mappedChildren.length === 0 ? childs : mappedChildren;
    };
    var pushTranslatedJSX = function pushTranslatedJSX(child, inner, mem, i) {
        var isVoid = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : false;
        if (child.dummy) {
            child.children = inner;
        } // needed on preact!
        mem.push(cloneElement(child, _object_spread_props(_object_spread({}, child.props), {
            key: i
        }), isVoid ? undefined : inner));
    };
    // reactNode (the jsx root element or child)
    // astNode (the translation string as html ast)
    // rootReactNode (the most outer jsx children array or trans components prop)
    var mapAST = function mapAST1(reactNode, astNode, rootReactNode) {
        var reactNodes = getAsArray(reactNode);
        var astNodes = getAsArray(astNode);
        return astNodes.reduce(function(mem, node, i) {
            var translationContent = node.children && node.children[0] && node.children[0].content;
            if (node.type === 'tag') {
                var tmp = reactNodes[parseInt(node.name, 10)]; // regular array (components or children)
                if (!tmp && rootReactNode.length === 1 && rootReactNode[0][node.name]) {
                    tmp = rootReactNode[0][node.name];
                }
                if (!tmp) {
                    tmp = {};
                }
                var child = Object.keys(node.attrs).length !== 0 ? mergeProps({
                    props: node.attrs
                }, tmp) : tmp;
                var isElement = isValidElement(child);
                var isValidTranslationWithChildren = isElement && hasChildren(node, true) && !node.voidElement;
                var isKnownComponent = (typeof children === "undefined" ? "undefined" : _type_of(children)) === 'object' && children !== null && Object.hasOwnProperty.call(children, node.name);
                if (typeof child === 'string') {
                    mem.push(child);
                } else if (hasChildren(child) || // the jsx element has children -> loop
                isValidTranslationWithChildren // valid jsx element with no children but the translation has -> loop
                ) {
                    var inner = renderInner(child, node, rootReactNode);
                    pushTranslatedJSX(child, inner, mem, i);
                } else if (Number.isNaN(parseFloat(node.name))) {
                    if (isKnownComponent) {
                        var inner1 = renderInner(child, node, rootReactNode);
                        pushTranslatedJSX(child, inner1, mem, i, node.voidElement);
                    } else if (keepArray.indexOf(node.name) > -1) {
                        if (node.voidElement) {
                            mem.push(createElement(node.name, {
                                key: "".concat(node.name, "-").concat(i)
                            }));
                        } else {
                            var inner2 = mapAST(reactNodes /* wrong but we need something */ , node.children, rootReactNode);
                            mem.push(createElement(node.name, {
                                key: "".concat(node.name, "-").concat(i)
                            }, inner2));
                        }
                    } else if (node.voidElement) {
                        mem.push("<".concat(node.name, " />"));
                    } else {
                        var inner3 = mapAST(reactNodes /* wrong but we need something */ , node.children, rootReactNode);
                        mem.push("<".concat(node.name, ">").concat(inner3, "</").concat(node.name, ">"));
                    }
                } else if ((typeof child === "undefined" ? "undefined" : _type_of(child)) === 'object' && !isElement) {
                    var content = node.children[0] ? translationContent : null;
                    // v1
                    // as interpolation was done already we just have a regular content node
                    // in the translation AST while having an object in reactNodes
                    // -> push the content no need to interpolate again
                    if (content) {
                        mem.push(content);
                    }
                } else if (node.children.length === 1 && translationContent) {
                    // If component does not have children, but translation - has
                    // with this in component could be components={[<span class='make-beautiful'/>]} and in translation - 'some text <0>some highlighted message</0>'
                    mem.push(cloneElement(child, _object_spread_props(_object_spread({}, child.props), {
                        key: i
                    }), translationContent));
                } else {
                    mem.push(cloneElement(child, _object_spread_props(_object_spread({}, child.props), {
                        key: i
                    })));
                }
            } else if (node.type === 'text') {
                mem.push(node.content);
            }
            return mem;
        }, []);
    };
    // call mapAST with having react nodes nested into additional node like
    // we did for the string ast from translation
    // return the children of that extra node to get expected result
    var result = mapAST([
        {
            dummy: true,
            children: children || []
        }
    ], ast, getAsArray(children || []));
    return getChildren(result[0]);
};

//# sourceMappingURL=renderNodes.js.map